import React, { useState, useEffect } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Lock, AlertCircle, CheckCircle, Loader, CreditCard, Smartphone } from 'lucide-react';
import { formatPrice } from '../utils/currencyUtils';

const ExperiencePaymentForm = ({
  clientSecret,
  billingAddress,
  onSuccess,
  onError,
  amount,
  currency
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [initError, setInitError] = useState(false);

  // Handle any redirect-based payment return (Google Pay, Apple Pay, Alipay etc.)
  useEffect(() => {
    if (!stripe || !clientSecret) return;

    // Check if Stripe initialized correctly
    if (!stripe) {
      setInitError(true);
      setErrorMessage('Stripe failed to initialize. Please check your configuration.');
      return;
    }

    // If returning from a redirect (e.g. Alipay), retrieve the PaymentIntent status
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntentParam = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');

    if (paymentIntentParam && redirectStatus) {
      stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent, error }) => {
        // Clear processed params from URL to prevent sticky errors on refresh
        const url = new URL(window.location.href);
        url.searchParams.delete('payment_intent');
        url.searchParams.delete('redirect_status');
        window.history.replaceState({}, document.title, url.toString());

        if (error) {
          onError(error);
          return;
        }
        if (paymentIntent?.status === 'succeeded') {
          const pmId =
            typeof paymentIntent.payment_method === 'string'
              ? paymentIntent.payment_method
              : paymentIntent.payment_method?.id;
          onSuccess(paymentIntent.id, pmId);
        } else if (paymentIntent?.status === 'requires_payment_method') {
          onError({ message: 'Payment failed. Please try a different payment method.' });
        }
      });
    }
  }, [stripe, clientSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setErrorMessage('');

    try {
      // confirmPayment handles card, Google Pay, Apple Pay, Alipay, etc.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Return URL for redirect-based payment methods (Alipay etc.)
          return_url: `${window.location.origin}${window.location.pathname}`,
          payment_method_data: {
            billing_details: {
              name: billingAddress?.cardholderName || '',
              address: {
                line1: billingAddress?.street || '',
                city: billingAddress?.city || '',
                state: billingAddress?.stateProvince || '',
                postal_code: billingAddress?.postalCode || '',
                country: billingAddress?.countryCode || 'US'
              }
            }
          }
        },
        // Don't redirect for card/wallet payments that don't require redirect
        redirect: 'if_required'
      });

      if (error) {
        console.error('Payment error:', error);
        setErrorMessage(error.message);
        onError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        const pmId =
          typeof paymentIntent.payment_method === 'string'
            ? paymentIntent.payment_method
            : paymentIntent.payment_method?.id;
        onSuccess(paymentIntent.id, pmId);
      }
    } catch (err) {
      console.error('Payment exception:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
      onError(err);
    } finally {
      setProcessing(false);
    }
  };

  const paymentElementOptions = {
    layout: 'accordion',
    defaultValues: {
      billingDetails: {
        name: billingAddress.cardholderName,
        address: {
          country: billingAddress.countryCode || 'US',
          postal_code: billingAddress.postalCode
        }
      }
    },
    fields: {
      billingDetails: {
        name: 'never', // We collect name separately in billingAddress
        address: {
          country: 'never',
          postalCode: 'never'
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Display */}
      <div className="p-4 bg-slate-900 rounded-2xl border border-slate-800">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-slate-300 uppercase tracking-widest">
            Total Amount
          </span>
          <span className="text-2xl typography-section-title font-black text-white">
            {formatPrice(amount, currency)}
          </span>
        </div>
      </div>

      {/* Accepted Payment Methods Badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Accepted:</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Card */}
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md text-[10px] font-bold text-slate-600 uppercase tracking-wider">
            <CreditCard className="h-3 w-3" />
            Card
          </span>
          {/* Google Pay */}
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md text-[10px] font-bold text-slate-600">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none">
              <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4"/>
            </svg>
            Google Pay
          </span>
          {/* Apple Pay */}
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md text-[10px] font-bold text-slate-600">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Apple Pay
          </span>
          {/* Alipay */}
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md text-[10px] font-bold text-slate-600">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="#00AAEF">
              <path d="M21.422 15.358c-2.578-1.034-4.213-1.73-5.422-2.358.74-1.208 1.29-2.546 1.597-3.999H20V7.5h-4V6h-2v1.5h-4V6H8v1.5H4v1.5h12.957c-.342 1.75-.89 3.3-1.626 4.548A28.875 28.875 0 0 0 12 12.5a19.98 19.98 0 0 0-4.65.557C6.015 13.465 4.5 14.578 4.5 16.5 4.5 18.43 6.022 19.5 7.939 19.5c2.343 0 4.454-1.412 5.812-3.5.913.462 2.547 1.337 5.066 2.51a9 9 0 0 1-6.817 3.49C7.164 22 3 18.51 3 13.5 3 8.5 7.164 5 12 5s9 4 9 9c0 .45-.035.908-.094 1.358zM7.94 18c-1.143 0-1.94-.5-1.94-1.5s.797-1.858 2.066-2.236c.656-.189 1.378-.287 2.122-.287.628 0 1.259.058 1.879.164C10.842 16.697 9.438 18 7.94 18z"/>
            </svg>
            Alipay
          </span>
        </div>
      </div>

      {/* Stripe Payment Element – renders card + wallets automatically */}
      <div>
        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 ml-1">
          <CreditCard className="h-3 w-3 inline mr-1.5" />
          Payment Method
        </label>

        <div className={`rounded-xl border-2 border-slate-200 bg-white overflow-hidden transition-all duration-200 min-h-[100px] relative ${!paymentElementReady ? 'flex items-center justify-center' : 'p-4'}`}>
          {!stripe && !initError && (
             <div className="flex flex-col items-center gap-2 py-8">
               <Loader className="h-6 w-6 animate-spin text-slate-400" />
               <p className="text-xs text-slate-400">Initializing secure payment...</p>
             </div>
          )}
          
          {stripe && (
            <PaymentElement
              options={paymentElementOptions}
              onReady={() => setPaymentElementReady(true)}
              onLoadError={(event) => {
                console.error('Payment Element load error:', event.error);
                setErrorMessage(event.error.message);
                setInitError(true);
              }}
              onChange={(event) => {
                if (event.error) {
                  setErrorMessage(event.error.message);
                } else if (event.complete) {
                  setErrorMessage('');
                }
              }}
            />
          )}

          {initError && (
            <div className="p-8 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm font-bold text-red-600">Failed to load payment form</p>
              <p className="text-xs text-slate-500 mt-1">Please refresh the page or contact support.</p>
            </div>
          )}
        </div>

        {errorMessage && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>

      {/* Security Badges */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Lock className="h-4 w-4 text-green-600 flex-shrink-0" />
          <span>Your payment information is encrypted and secure</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
          <span>Protected by Stripe's industry-leading security</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Smartphone className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span>Google Pay & Apple Pay available on supported devices</span>
        </div>
      </div>

      {/* Test Card Info (only show in development) */}
      {import.meta.env.DEV && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-1">
          <p className="text-xs font-bold text-amber-800">🧪 Test Mode – Use test cards:</p>
          <p className="text-xs text-amber-700 font-mono">Card: 4242 4242 4242 4242 | Any future date | Any CVC</p>
          <p className="text-xs text-amber-700 font-mono">Alipay: use test redirect flow</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || processing || !paymentElementReady || initError}
        className="w-full relative overflow-hidden px-8 py-4 bg-slate-900 hover:bg-black text-white rounded-2xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-slate-900/40 hover:shadow-slate-900/60 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 group"
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader className="h-4 w-4 animate-spin" />
            Processing Payment...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Lock className="h-4 w-4" />
            Complete Payment – {formatPrice(amount, currency)}
          </span>
        )}

        {/* Animated shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </button>

      {/* Stripe Branding */}
      <div className="flex items-center justify-center pt-1">
        <div className="flex items-center space-x-2 px-4 py-2 bg-slate-900 rounded-lg border border-slate-700">
          <span className="inline-flex items-center px-3 py-1 rounded-md bg-[#635bff] shadow-sm">
            <span className="text-xs font-semibold tracking-wide text-white leading-none">
              stripe
            </span>
          </span>
          <span className="text-[10px] font-semibold text-slate-300 tracking-wider">
            Secure payments
          </span>
        </div>
      </div>
    </form>
  );
};

export default ExperiencePaymentForm;
